import { Box, Flex, Tooltip } from "@chakra-ui/react";
import clsx from "clsx";
import { useMemo, useRef } from "react";
import {
  MdArrowRight,
  MdCancel,
  MdOutlineErrorOutline,
  MdOutlineMessage,
} from "react-icons/md";
import { useParams } from "react-router-dom";
import { Edge, Node } from "reactflow";

import { useShowToast } from "@/components/toast";
import { Button } from "@/design/components/button";
import { IconButton } from "@/design/components/icon-button";
import { useAppDispatch, useAppSelector } from "@/reduxHooks.ts";
import { ModalTypes, openModal } from "@/slices/modal-slice";

import { getPendingRequests, selectPanel, showPanel } from "../..";
import {
  useGetWFActiveUserQuery
} from "../../api/workflow-api";
import { useRunWorkflow } from "../../hooks/useRunWorkflow";
import { useWorkflowRunTrigger } from "../../hooks/workflowRunTrigger";
import {
  currentWfActiveUser,
  currentWorkflowId,
  currentWorkflowRunId,
  getEditingAllowed,
  getWorkflowState,
  setEditingAllowed,
  workflowStatus as wfRunStatus,
  workflowRunningStatus,
} from "../../redux/workflow-slice";
import { EventType, WorkflowEvent } from "../../types/workflow-types";
import {
  NODE_STATUS,
  NODE_STATUS_ICONS,
  WORKFLOW_PANELS,
} from "../../utils/constants";
import { validateNodeConfig } from "../../utils/validations";

export const RunAndLogButtons = ({
  hasEditAccess,
  nodes,
  edges,
}: {
  hasEditAccess: boolean;
  nodes: Node[];
  edges: Edge[];
}) => {
  const dispatch = useAppDispatch();
  const toast = useShowToast();
  const failureCountRef = useRef(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const params = useParams();
  const workflowId = useAppSelector(currentWorkflowId);
  const workflowRunId = useAppSelector(currentWorkflowRunId);
  const currentWFRunStatus = useAppSelector(workflowRunningStatus);
  const wfnodeStatus = useAppSelector(wfRunStatus);
  const { runWorkflow, isLoading } = useRunWorkflow();
  const currentUser = useAppSelector(currentWfActiveUser);
  const workflowState = useAppSelector(getWorkflowState);
  const isEditingAllowed = useAppSelector(getEditingAllowed) ?? false;
  const logsPanel = useAppSelector(selectPanel(WORKFLOW_PANELS.LogsPanel));
  const { data: activeUserData } = useGetWFActiveUserQuery(
    { analysisId: params.analysisId! },
    { skip: !params.analysisId }
  );
  const pendingRequests = useAppSelector(getPendingRequests);

  const isWorkflowInProgress =
    currentWFRunStatus === NODE_STATUS.RUNNING ||
    currentWFRunStatus === NODE_STATUS.CANCELLING;

  const isWorkflowPartiallyRunning =
    currentWFRunStatus === NODE_STATUS.PARTIALLY_RUNNING;

  const workflowErrors = nodes.filter(
    (node: Node) => !validateNodeConfig(node, nodes, edges).isValid
  );
  const hasError = useMemo(
    () => isWorkflowPartiallyRunning || workflowErrors.length > 0,
    [workflowErrors, isWorkflowPartiallyRunning]
  );

  const hasBlockingEvents = useMemo(() => {
    return (
      activeUserData?.data?.results?.events?.some(
        (event: WorkflowEvent) =>
          event.eventType === EventType.ACTIONABLE_BLOCKER
      ) ?? false
    );
  }, [activeUserData?.data?.results?.events]);

  const isDisabled = useMemo(
    () =>
      !isEditingAllowed ||
      isLoading ||
      nodes.length === 0 ||
      (hasBlockingEvents && logsPanel.isVisible) ||
      pendingRequests.length > 0,
    [
      isLoading,
      nodes.length,
      isEditingAllowed,
      hasBlockingEvents,
      logsPanel.isVisible,
      pendingRequests.length,
    ]
  );

  const isTerminateEnabled =
    (hasEditAccess && (isLoading || wfnodeStatus?.terminateWorkflowEnabled)) ??
    false;

  const notificationStatus = useMemo(() => {
    const events =
      (activeUserData?.data?.results?.events as WorkflowEvent[]) || [];
    const hasBlocker = events.some(
      (event) => event.eventType === EventType.ACTIONABLE_BLOCKER
    );
    const hasWarning = events.some(
      (event) => event.eventType === EventType.ACTIONABLE_WARNING
    );

    if (hasBlocker) return "blocker";
    if (hasWarning) return "warning";
    return null;
  }, [activeUserData?.data?.results?.events]);

  const openworkflowErrorsModal = () => {
    dispatch(
      openModal({
        modalType: ModalTypes.WORKFLOW_ERRORS,
        modalProps: {
          nodes: nodes,
          edges: edges,
        },
      })
    );
  };

  useWorkflowRunTrigger(hasError, openworkflowErrorsModal);

  const onTerminate = () => {
    toast({
      title: "Run terminated successfully",
    });
    dispatch(setEditingAllowed(true));
  };

  const terminateWorkflow = () => {
    dispatch(
      openModal({
        modalType: ModalTypes.CONFIRM_TERMINATE,
        modalProps: {
          currentUser,
          workflowState: workflowState,
          workflowId: params.editorId!,
          workflowRunId: workflowRunId!,
        },
      })
    );
  };

  const openLogsPanel = () => {
    dispatch(showPanel({ panel: WORKFLOW_PANELS.LogsPanel }));
  };
  const workflowStatusText = useMemo(() => {
    if (currentWFRunStatus === NODE_STATUS.CANCELLING) {
      return {
        disabled: "Cancelling Workflow",
        running: "Terminating Workflow",
      };
    }
    return {
      disabled: "Building workflow, please wait...",
      running: "Running Workflow",
    };
  }, [currentWFRunStatus]);

  const onRunClicked = () => {
    if (hasError) {
      openworkflowErrorsModal();
    } else {
      failureCountRef.current = 0;
      runWorkflow.bind(null, workflowId!, {})();
    }
  };

  return (
    <Flex align="center" gap={1}>
      <Box pos="relative">
        <IconButton
          aria-label="view logs"
          variant="ghost"
          size="sm"
          colorScheme="dark"
          onClick={openLogsPanel}
          icon={<MdOutlineMessage />}
        />
        {notificationStatus && (
          <Box
            className={clsx(
              notificationStatus === "blocker" && "bg-red-500",
              notificationStatus === "warning" && "bg-yellow-500"
            )}
            pos="absolute"
            top="-1px"
            right="-1px"
            w="8px"
            h="8px"
            borderRadius="full"
          />
        )}
      </Box>
      {isWorkflowInProgress ? (
        <Flex className="gap-2 pr-2" align="center" justify={"end"}>
          <Tooltip
            isDisabled={isTerminateEnabled}
            label={workflowStatusText.disabled}
            placement="bottom"
          >
            <Button
              className="ml-2 disabled:hover:!bg-red-400"
              onClick={terminateWorkflow}
              size="sm"
              isDisabled={!isTerminateEnabled}
              variant="outline"
              isLoading={isLoading}
              color="red.600"
              colorScheme="customRed"
              rightIcon={<MdCancel />}
            >
              Terminate Run
            </Button>
          </Tooltip>
          <Box className="text-gray-800 pb-1">
            {NODE_STATUS_ICONS[currentWFRunStatus as NODE_STATUS].icon}
          </Box>
          <Box className="text-gray-800 text-xs">
            {workflowStatusText.running}
          </Box>
        </Flex>
      ) : (
        <Tooltip
          hasArrow
          isDisabled={isEditingAllowed}
          label="Cannot run workflow in View Mode."
          placement="bottom"
        >
          <Button
            ref={buttonRef}
            onClick={onRunClicked}
            size="sm"
            className="disabled:cursor-not-allowed"
            variant={hasError ? "outline" : "solid"}
            isLoading={isLoading}
            isDisabled={isDisabled}
            colorScheme="dark"
            rightIcon={hasError ? <MdOutlineErrorOutline /> : <MdArrowRight />}
          >
            Run WorkFlow
          </Button>
        </Tooltip>
      )}
    </Flex>
  );
};
